// The chess of a solution, apart from any board.
//
// Everything the board needs to know about the lines it is given — what they
// are, which of them the solver has to play, what castling means in them, and
// whether they can be played at all — lives here, in functions of their
// inputs and nothing else. The board imports this module; so do the tests and
// the library check, which runs it over every puzzle in the live library
// before a deploy. What is tested is what runs.

import { Chess, type Move } from "chess.js";
import { expand, toUci, type Slot } from "./notation.ts";

export type { Slot };

/** One line of a solution from the puzzle's position to a leaf. */
export interface Branch {
  moves: Slot[];
}

export interface Fault {
  kind: string;
  detail: string;
}

// --- MOVES ---

/** The move a slot stands for: a slot of several accepted spellings is
 *  walked by its first. */
export const first = (slot: Slot | undefined): string => (Array.isArray(slot) ? (slot[0] ?? "") : (slot ?? ""));

/** Two slots are the same by value: two arrays are never the same array. */
export const same = (a: Slot | undefined, b: Slot | undefined): boolean => String(a) === String(b);

export const isUci = (uci: unknown): uci is string => typeof uci === "string" && /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(uci);

export function playUci(game: Chess, uci: string): Move | null {
  if (!isUci(uci)) return null;
  return game.move({ from: uci.substring(0, 2), to: uci.substring(2, 4), promotion: uci.substring(4, 5) || "q" });
}

/** Enough of a FEN to recognise a position across two implementations. */
export function positionKey(fen: string): string {
  const fields = String(fen).split(/\s+/);
  return `${fields[0]} ${fields[1]}`;
}

/** The side to move in a position. */
export const sideOf = (fen: string): "w" | "b" => (String(fen).split(/\s+/)[1] === "b" ? "b" : "w");

// --- CASTLING, AS THE BOARD'S RULES WRITE IT ---
//
// UCI writes castling two ways: the king's own move, `e1g1`, and
// king-takes-rook, `e1h1`, which Chess960 needs and which the Lichess
// evaluation database — where the opening book's lines come from — uses
// throughout. chess.js knows only the first. Every line is rewritten from the
// position it starts in before anything plays it.

export function standardUci(game: Chess, uci: string): string {
  if (typeof uci !== "string" || uci.length < 4) return uci;
  const from = uci.substring(0, 2);
  const to = uci.substring(2, 4);
  const king = game.get(from);
  const rook = game.get(to);
  if (king && rook && king.type === "k" && rook.type === "r" && king.color === rook.color) {
    return from + (to[0]! > from[0]! ? "g" : "c") + from[1];
  }
  return uci;
}

function tryChess(fen: string): Chess | null {
  try {
    return new Chess(fen);
  } catch {
    return null;
  }
}

/**
 * A line rewritten move by move from the position it starts in, and how many
 * of its moves can be played. What cannot be played is left as it came, and
 * the check says so out loud. One pass does both: the library check reads a
 * hundred thousand lines.
 */
export function readLine(fromFen: string, ucis: readonly unknown[] | null | undefined): { line: string[]; reach: number } {
  const line: string[] = [];
  let reach = 0;
  let game = tryChess(fromFen);
  for (const raw of ucis || []) {
    if (!game || typeof raw !== "string") {
      line.push(raw as string);
      continue;
    }
    const uci = standardUci(game, raw);
    line.push(uci);
    if (playUci(game, uci)) reach += 1;
    else game = null;
  }
  return { line, reach };
}

export const standardLine = (fromFen: string, ucis: readonly unknown[] | null | undefined): string[] => readLine(fromFen, ucis).line;

/** How many moves of a line can be played, from the start. */
export function playable(fromFen: string, ucis: readonly string[] | null | undefined): number {
  const game = tryChess(fromFen);
  if (!game) return 0;
  let n = 0;
  for (const uci of ucis || []) {
    if (!playUci(game, uci)) break;
    n += 1;
  }
  return n;
}

/** The position a line reaches, or null if it cannot be played. */
export function fenAfter(fromFen: string, ucis: readonly Slot[] | null | undefined): string | null {
  const game = tryChess(fromFen);
  if (!game) return null;
  for (const uci of ucis || []) if (!playUci(game, first(uci))) return null;
  return game.fen();
}

// --- WHAT HAS TO BE SOLVED ---
//
// A solution is a tree. Where the solver is to move, its branches are
// alternatives: any one of them answers the question. Where the opponent is
// to move, its branches are variations: each is a reply the solver has to
// meet. The board is given the tree as a list of lines from the root, and
// which of the two a pair of lines is follows from where they part. The
// solver moves first, so their moves are the even plies: lines that part at
// an even ply are alternatives, lines that part at an odd ply are variations.
//
// This used to be a tag on each line saying which group of alternatives it
// belonged to. The tag was a property on an array, and code that built a new
// array — rewriting castling, one morning — dropped it without a sound: the
// alternatives of one question became "variations" of it, an accepted move
// was marked wrong, and the next "variation" started by playing the solver's
// move for them and handing them the opponent's pieces. Where lines part is
// part of the lines themselves, and cannot fall off.

/** The ply at which two lines part: the length of what they share. */
export function partsAt(a: readonly Slot[], b: readonly Slot[]): number {
  let k = 0;
  while (k < a.length && k < b.length && same(a[k], b[k])) k += 1;
  return k;
}

/** Whether two lines are answers to the same question. */
export const alternatives = (a: readonly Slot[], b: readonly Slot[]): boolean => partsAt(a, b) % 2 === 0;

/**
 * The lines the solver has to play, given the ones already chosen: those,
 * then every other line that is a variation of each of them, in order.
 * Before anything is chosen it is the plan under the first alternative at
 * every choice, which is the one a line list names first.
 */
export function plan(branches: readonly Branch[], chosen: readonly number[] = []): number[] {
  const out: number[] = [];
  const fits = (i: number) => out.every((j) => !alternatives(branches[i]!.moves, branches[j]!.moves));
  for (const i of chosen) if (!out.includes(i)) out.push(i);
  for (let i = 0; i < branches.length; i++) if (!out.includes(i) && fits(i)) out.push(i);
  return out;
}

/** The next line to play once `solved` are done, or null. */
export function nextToSolve(branches: readonly Branch[], solved: readonly number[]): number | null {
  const next = plan(branches, solved).find((i) => !solved.includes(i));
  return next === undefined ? null : next;
}

/** "Variation 2 of 3", as numbers: where the current line stands. */
export function progress(branches: readonly Branch[], solved: readonly number[], current: number): { at: number; of: number } {
  const chosen = solved.includes(current) ? solved : solved.concat([current]);
  const all = plan(branches, chosen);
  return { at: all.indexOf(current) + 1, of: all.length };
}

export function accepts(slot: Slot | undefined, uci: string): boolean {
  return Array.isArray(slot) ? slot.includes(uci) : slot === uci;
}

/**
 * The solver played `uci` at ply `at` of the current line, and it is not the
 * move that line expects. If another line takes the same path so far,
 * accepts that move there, and is still open — not an alternative to any line
 * already solved — it is the line they chose: its index, else -1.
 */
export function siblingFor(branches: readonly Branch[], current: number, solved: readonly number[], at: number, uci: string): number {
  const path = branches[current]!.moves;
  for (let i = 0; i < branches.length; i++) {
    if (i === current) continue;
    const moves = branches[i]!.moves;
    if (moves.length <= at) continue;
    if (!accepts(moves[at], uci)) continue;
    let samePath = true;
    for (let k = 0; k < at; k++) {
      if (!same(moves[k], path[k])) {
        samePath = false;
        break;
      }
    }
    if (!samePath) continue;
    if (solved.some((j) => j !== current && alternatives(moves, branches[j]!.moves))) continue;
    return i;
  }
  return -1;
}

/**
 * Every move that answers at ply `at` of the line `moves`: its own, and those
 * of every other line that took the same path to here — the alternatives,
 * whatever line they were listed under. What a hint shows.
 */
export function answersAt(branches: readonly Branch[], moves: readonly Slot[], at: number): string[] {
  const out: string[] = [];
  for (const branch of branches) {
    const line = branch.moves;
    if (line.length <= at || partsAt(line, moves) < at) continue;
    for (const uci of ([] as string[]).concat(line[at]!)) if (!out.includes(uci)) out.push(uci);
  }
  return out;
}

// --- READING WHAT THE SERVER SENT ---

/**
 * The lines of a puzzle, from the moves string it came with: SAN or UCI, with
 * [variations] and {alternatives}. Each line is `{ moves }`, castling written
 * as the board plays it, duplicates dropped. `faults` lists what is wrong with
 * them, for whoever is listening — the board reports it, the library check
 * fails the deploy.
 */
export function branchesOf(fen: string, movesString: string | null | undefined): { branches: Branch[]; faults: Fault[] } {
  const faults: Fault[] = [];
  const text = String(movesString || "");
  const message = (e: unknown) => String(e instanceof Error ? e.message : e);
  let uci = text;
  try {
    uci = toUci(fen, text);
  } catch (e) {
    faults.push({ kind: "moves-unreadable", detail: message(e) });
  }
  let raw: Slot[][] = [];
  try {
    raw = expand(uci);
  } catch (e) {
    faults.push({ kind: "moves-unreadable", detail: message(e) });
  }
  const branches: Branch[] = [];
  for (const line of raw) {
    const moves = standardBranch(fen, line);
    // A line listed twice is one line. It happened: castling offered as both
    // `e1g1` and `e1h1`, and shown as "O-O" twice.
    if (branches.some((b) => b.moves.length === moves.length && partsAt(b.moves, moves) === moves.length)) {
      faults.push({ kind: "duplicate-line", detail: moves.join(" ") });
      continue;
    }
    branches.push({ moves });
  }
  if (!branches.length) {
    faults.push({ kind: "no-lines", detail: text });
    return { branches: [{ moves: [] }], faults };
  }
  // A move the converter could not turn into one — illegal where it stands,
  // or not chess — leaves the string as it came, so nothing in it can be
  // trusted: that is one fault, about the whole of it.
  const unread = branches.some((b) => b.moves.some((slot) => ([] as string[]).concat(slot).some((m) => !isUci(m))));
  if (unread) {
    faults.push({ kind: "moves-unreadable", detail: text });
    return { branches, faults };
  }
  branches.forEach((branch, index) => {
    const reach = playable(fen, branch.moves.map(first));
    if (reach < branch.moves.length) {
      faults.push({ kind: "line-unplayable", detail: `line ${index + 1}, ply ${reach + 1}: ${first(branch.moves[reach])}` });
    }
    // A line ends on the solver's move: one that ends on the opponent's
    // leaves them to move with nothing to find.
    if (branch.moves.length % 2 === 0) {
      faults.push({ kind: "line-ends-on-reply", detail: `line ${index + 1}: ${branch.moves.join(" ")}` });
    }
  });
  for (let i = 0; i < branches.length; i++) {
    for (let j = 0; j < branches.length; j++) {
      if (i === j) continue;
      const a = branches[i]!.moves;
      const b = branches[j]!.moves;
      if (a.length < b.length && partsAt(a, b) === a.length) {
        faults.push({ kind: "line-inside-another", detail: `${a.join(" ")} / ${b.join(" ")}` });
      }
    }
  }
  return { branches, faults };
}

/** One line of a solution, castling rewritten, its slots kept slots. */
export function standardBranch(fromFen: string, branch: readonly Slot[]): Slot[] {
  const out: Slot[] = [];
  let game = tryChess(fromFen);
  for (const entry of branch || []) {
    const options = ([] as string[]).concat(entry).map((uci) => (game ? standardUci(game, uci) : uci));
    out.push(Array.isArray(entry) ? options : options[0]!);
    if (!game || !playUci(game, options[0]!)) game = null;
  }
  return out;
}

/** What the server says a wrong move runs into. */
export interface Refutation {
  uci: string;
  san?: string;
  line?: string[];
  score?: Score | null;
}

/** A score, from the solver's side unless said otherwise. */
export interface Score {
  kind: "cp" | "mate";
  value: number;
}

/** What follows a solved line: the move that holds, and the one people play. */
export interface Epilogue {
  from: string;
  line?: string[];
  common?: string[];
  common_share?: number;
  score?: Score | null;
}

export interface PuzzleData {
  fen: string;
  widget_moves: string;
  refutations?: Refutation[];
  epilogues?: Epilogue[];
  played_san?: string;
}

/**
 * Everything that can be wrong with a puzzle before anyone sees it: its
 * lines, its refutations and its continuations, each played from where it
 * starts with the same rewriting the board applies.
 */
export function checkPuzzle(puzzle: PuzzleData): Fault[] {
  const fen = puzzle.fen;
  const { branches, faults } = branchesOf(fen, puzzle.widget_moves);

  // The move that was played is the question. It cannot be the answer.
  if (puzzle.played_san) {
    const game = new Chess(fen);
    const played = game.move(puzzle.played_san, { sloppy: true });
    if (!played) {
      faults.push({ kind: "played-move-unplayable", detail: puzzle.played_san });
    } else {
      const uci = played.from + played.to + (played.promotion || "");
      if (branches.some((b) => b.moves.length && accepts(b.moves[0], uci))) {
        faults.push({ kind: "played-move-is-an-answer", detail: puzzle.played_san });
      }
    }
  }

  for (const refutation of puzzle.refutations || []) {
    const { line, reach } = readLine(fen, [refutation.uci].concat(refutation.line || []));
    if (reach === 0) {
      faults.push({ kind: "refutation-unplayable", detail: String(refutation.uci) });
    } else if (reach < line.length) {
      faults.push({ kind: "refutation-line-unplayable", detail: `${refutation.uci}: ply ${reach + 1} of ${line.join(" ")}` });
    }
  }

  const ends = branches.map((b) => fenAfter(fen, b.moves.map(first))).filter((f): f is string => Boolean(f));
  for (const epilogue of puzzle.epilogues || []) {
    if (!epilogue || !epilogue.from) continue;
    const from = ends.find((end) => positionKey(end) === positionKey(epilogue.from));
    // A continuation for a position no line reaches is never shown, which is
    // waste, not harm.
    if (!from) continue;
    for (const which of ["line", "common"] as const) {
      const { line, reach } = readLine(from, epilogue[which] || []);
      if (reach < line.length) {
        faults.push({ kind: "continuation-unplayable", detail: `${which}, ply ${reach + 1} of ${line.join(" ")}` });
      }
    }
  }
  return faults;
}
